import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Shield, Eye, EyeOff, AlertCircle, CheckCircle2,
} from "lucide-react";
import BackToHomepageLink from "@/components/shared/BackToHomepageLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("This reset link is missing its token.");
      return;
    }
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: pw,
      });
      setDone(true);
      // Auto-redirect to login after 3s so the user has a clear cue.
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Reset failed. The link may be invalid or expired.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B1120] p-6"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-6">
          <BackToHomepageLink
            testId="reset-back-to-homepage"
            label="Back to Homepage"
          />
        </div>
        <Link
          to="/"
          className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity w-fit"
          data-testid="reset-logo-link"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-slate-900" />
          </div>
          <div>
            <h1 className="font-barlow font-bold text-xl text-slate-50">
              Sentient Vault
            </h1>
            <span className="text-[9px] text-amber-400/70 tracking-[0.25em] uppercase font-manrope">
              Obligation-Enforced Finance
            </span>
          </div>
        </Link>

        {!done ? (
          <>
            <h2 className="font-barlow font-semibold text-2xl text-slate-50 mb-1">
              Create a new password
            </h2>
            <p className="text-sm text-slate-500 mb-8 leading-relaxed">
              Choose a new password for your Sentient Vault account.
              You will need to sign in again afterwards on every
              device.
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs text-slate-400 uppercase tracking-wider">
                  New password
                </Label>
                <div className="relative">
                  <Input
                    type={show ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="bg-slate-900/50 border-slate-700 text-slate-100 h-11 pr-10"
                    data-testid="reset-new-password-input"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="reset-toggle-show"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-slate-400 uppercase tracking-wider">
                  Confirm password
                </Label>
                <Input
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-slate-100 h-11"
                  data-testid="reset-confirm-password-input"
                  minLength={8}
                />
              </div>

              {error && (
                <div
                  className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                  data-testid="reset-error"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !pw || !confirm}
                className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-lg transition-all active:scale-[0.98]"
                data-testid="reset-submit-btn"
              >
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          </>
        ) : (
          <div
            className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6"
            data-testid="reset-success"
          >
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-3" />
            <h2 className="font-barlow font-semibold text-xl text-slate-50 mb-2">
              Your password has been reset
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              Please sign in again. We’ll redirect you in a moment.
            </p>
            <Link
              to="/login"
              className="inline-block mt-6 text-sm text-amber-400 hover:text-amber-300"
              data-testid="reset-success-login-link"
            >
              Continue to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
